/**
 * Feature flag API client.
 *
 * All endpoints are tenant-scoped (`/api/v1/tenants/{tenantId}/features`).
 * Reads are open to any authenticated user; writes require admin role
 * (the backend enforces this — the UI also hides the toggle controls
 * for non-admins as a courtesy).
 */
import { buildApiUrl, tenantApiPrefix } from '../config';

export interface FeatureRead {
  key: string;
  name: string;
  description: string;
  category: 'ai_capabilities' | 'data' | 'admin' | 'integrations' | 'experimental';
  lifecycle: 'ga' | 'beta' | 'deprecated';
  default_enabled: boolean;
  requires_features: string[];
  config_schema: Record<string, any> | null;
  /** Effective state — false if either the toggle is off OR a dependency is unmet. */
  enabled: boolean;
  /** What the toggle reflects (independent of dependency check). */
  explicit_enabled: boolean;
  dependencies_satisfied: boolean;
  config: Record<string, any>;
  enabled_at: string | null;
  enabled_by_user_id: string | null;
  updated_at: string | null;
  has_row: boolean;
}

export interface FeatureAuditEntry {
  audit_id: string;
  feature_key: string;
  action: 'enable' | 'disable' | 'config_update';
  previous_enabled: boolean | null;
  new_enabled: boolean | null;
  previous_config: Record<string, any> | null;
  new_config: Record<string, any> | null;
  actor_user_id: string | null;
  actor_kind: 'tenant_admin' | 'platform_admin' | 'system';
  reason: string | null;
  created_at: string;
}

export interface FeaturePatch {
  enabled?: boolean;
  config?: Record<string, any>;
  reason?: string;
}

const authHeaders = (): Record<string, string> => {
  const token = window.localStorage.getItem('klaris_jwt');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

async function readJson<T>(resp: Response): Promise<T> {
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    const detail = (data && (data.detail || data.message)) || `Request failed (${resp.status})`;
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
  return data as T;
}

export async function listFeatures(tenantId: string): Promise<FeatureRead[]> {
  const resp = await fetch(buildApiUrl(`${tenantApiPrefix(tenantId)}/features`), {
    headers: authHeaders(),
    cache: 'no-store',
  } as RequestInit);
  return readJson<FeatureRead[]>(resp);
}

export async function getFeature(tenantId: string, key: string): Promise<FeatureRead> {
  const resp = await fetch(
    buildApiUrl(`${tenantApiPrefix(tenantId)}/features/${encodeURIComponent(key)}`),
    {
      headers: authHeaders(),
      cache: 'no-store',
    } as RequestInit,
  );
  return readJson<FeatureRead>(resp);
}

export async function patchFeature(
  tenantId: string,
  key: string,
  patch: FeaturePatch,
): Promise<FeatureRead> {
  const resp = await fetch(
    buildApiUrl(`${tenantApiPrefix(tenantId)}/features/${encodeURIComponent(key)}`),
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(patch),
    } as RequestInit,
  );
  return readJson<FeatureRead>(resp);
}

export async function listFeatureAudit(
  tenantId: string,
  key: string,
  limit = 50,
): Promise<FeatureAuditEntry[]> {
  const resp = await fetch(
    buildApiUrl(
      `${tenantApiPrefix(tenantId)}/features/${encodeURIComponent(key)}/audit?limit=${limit}`,
    ),
    {
      headers: authHeaders(),
      cache: 'no-store',
    } as RequestInit,
  );
  return readJson<FeatureAuditEntry[]>(resp);
}
